import React, { FC } from 'react';
import styled from 'styled-components';
import { forDesktop } from '@westwing/ui-kit/breakPoints';
import { body2, body2Bold } from '@westwing/ui-kit/typography';
import AccordionMenu from '@westwing/ui-kit/Accordion';
import useBellaTranslator from '../../hooks/useTranslator';
import useConfig from '../../hooks/useConfig';
import ValueProps, { Align } from '../Generic/ValueProps';
import LineSeparator from '../Generic/LineSeparator';
import ContactAndFAQ from './ContactAndFAQ';
import AppStore from './AppStore';
import SocialMedia from './SocialMedia';
import { UsefulInformation } from './UsefulInformation';
import WestwingInternational from './WestwingInternational';
import { FooterModeType, useLayoutByPage } from 'Client/hooks/useLayoutByPage';
import CartFooter from 'Client/components/OneFooter/CartFooter';

const StyledFooterWrapper = styled.div`
    display: block;
    background: ${({ theme }) => theme.colors.turquoise10};
    border-bottom: 5px solid ${({ theme }) => theme.colors.turquoise100};
    margin-top: 20px;
    ${forDesktop`
        margin-top: 40px;
    `}
`;

const PaddedContentArea = styled.div`
    display: flex;
    flex-direction: column;
    padding: ${({ theme }) => `0 ${theme.mobilePagePadding}`};

    ${forDesktop`
        padding: 0;
    `}
`;

const MobileComponent = styled.div`
    display: block;
    ${forDesktop`
        display: none;
    `}
`;

const StyledTitle = styled.span`
    ${body2}
`;

const DesktopComponentOuter = styled.div`
    display: none;
    ${forDesktop`
        display: block;
    `}
`;

const DesktopComponentInner = styled.div`
    padding: 45px 0 37px;
    display: flex;
    justify-content: center;
    height: auto;
    a:hover {
        opacity: 0.5;
    }
`;

const DefaultWrapper = styled.div`
    min-width: 150px;
    width: auto;
`;

const WestwingInternationalWrapper = styled.div`
    width: 341px;
    margin: 0 41px;
`;

const Title = styled.div`
    ${body2Bold}
    color: ${({ theme }) => theme.colors.charcoal100};
    letter-spacing: normal;
`;

const StyledDesktopWrapper = styled.div`
    display: none;
    ${forDesktop`
        display: block;
    `}
`;

export const FooterValueProps = styled(ValueProps)`
    max-width: 440px;
    margin: 0 auto 20px auto;
    padding-top: 20px;
`;

const OneFooter: FC = () => {
    const t = useBellaTranslator();
    const contact = t('Contacts_and_FAQ');
    const international = t('WestwingNow_worldwide');
    const usefulInformation = t('Footer_Useful_information');
    const config = useConfig();
    const {
        links: { android, apple, appsPromoPage, instagram, pinterest, youtube, facebook2 },
    } = config;

    const { footerMode } = useLayoutByPage();
    if (footerMode === FooterModeType.NONE) {
        return <></>;
    }

    if (footerMode === FooterModeType.CART) {
        return <CartFooter />;
    }

    return (
        <StyledFooterWrapper>
            <FooterValueProps align={Align.center} />
            <PaddedContentArea>
                <LineSeparator />
                <MobileComponent>
                    <AppStore appleLink={apple} androidLink={android} />
                </MobileComponent>
                <StyledDesktopWrapper>
                    <AppStore appleLink={appsPromoPage} androidLink={appsPromoPage} />
                </StyledDesktopWrapper>
                <DesktopComponentOuter>
                    <LineSeparator />
                    <DesktopComponentInner>
                        <DefaultWrapper>
                            <Title>{contact}</Title>
                            <ContactAndFAQ />
                        </DefaultWrapper>
                        <WestwingInternationalWrapper>
                            <Title>{international}</Title>
                            <WestwingInternational />
                        </WestwingInternationalWrapper>
                        <DefaultWrapper>
                            <Title>{usefulInformation}</Title>
                            <UsefulInformation />
                        </DefaultWrapper>
                    </DesktopComponentInner>
                </DesktopComponentOuter>
                <MobileComponent>
                    <AccordionMenu renderTitle={() => <StyledTitle>{contact}</StyledTitle>}>
                        <ContactAndFAQ />
                    </AccordionMenu>
                    <AccordionMenu renderTitle={() => <StyledTitle>{international}</StyledTitle>}>
                        <WestwingInternational />
                    </AccordionMenu>
                    <AccordionMenu renderTitle={() => <StyledTitle>{usefulInformation}</StyledTitle>}>
                        <UsefulInformation />
                    </AccordionMenu>
                </MobileComponent>
                <LineSeparator />
                <SocialMedia
                    instagramLink={instagram}
                    pinterestLink={pinterest}
                    youtubeLink={youtube}
                    facebookLink={facebook2}
                />
                <LineSeparator />
            </PaddedContentArea>
        </StyledFooterWrapper>
    );
};

export default OneFooter;
